/**
 * Feature switches and editor visibility flags (plan 6p; Step 3, piece 22).
 *
 * Plain, local constants: no network call, no hosted Console asset and no environment variable
 * per flag. A flag changes by release: edit the value here, in the PR that ships (or switches off)
 * the feature, with its test. Code reads a flag by importing it:
 *
 *   import { features } from '../../config/features';
 *   if (features.paidPlans) { … }
 *
 * The server reads the same file (server/domain/access/), so this one stays CommonJS: the server
 * runs untransformed.
 *
 * If a flag ever has to differ between staging and production, derive it here from the one switch
 * that already tells them apart (REACT_APP_BETA_MODE), never from a new variable.
 *
 * Tests override a flag with jest.mock (see conventions.md, "A feature flag").
 */

// Freeze nested objects too, so no code can switch a flag at run time.
const deepFreeze = obj => {
  Object.values(obj).forEach(value => {
    if (value && typeof value === 'object') {
      deepFreeze(value);
    }
  });
  return Object.freeze(obj);
};

/**
 * Phase 1 switches (plan 6p). The code stays; the switch hides it.
 */
const features = deepFreeze({
  // Paid plans, billing, upgrade, the trial (B5), seat limits, the Creator Pro screens, pop-ups
  // and emails. Off for Phase 1.
  paidPlans: false,
  // A person's Free limits still apply while paid plans are off (S3-18; C19: 10 attendee-hours, 15- or
  // 30-minute episodes, 10 people, 10 sends). False would give the Pro row's limits.
  freeLimitsWithoutPaidPlans: true,
  // Recording and replay, off until a consent flow exists.
  recordingAndReplay: false,

  // The cut list: each ships switched off if its decision is late (decisions by about 13 Nov).
  // Buying the Production package (its contents and price). Not built yet.
  productionPackagePurchase: false,
  // The Founding supporter page (the accountant). Not built yet.
  founderSupporterPage: false,
  // Acting as an organization and the shared inbox (DPO review). Not built yet.
  actingAsOrganization: false,
  // The tax line per payment type (the accountant). Today's checkout shows Stripe's tax line.
  taxLine: true,
  // Free organization limits: campaigns per month. Enforced today. Seats go with paidPlans (6p).
  freeOrgLimits: true,
});

/**
 * Which steps and cards each editor shows (piece 22). A step is keyed by its tab id; only a step
 * set to `true` shows. Today's steps are on. The slots for the redesigned editors
 * (LiveRoomEditor and ProductionEditor boards) are off until their slice ships, and that slice
 * turns them on.
 */
const editorFlags = deepFreeze({
  liveRoom: {
    steps: {
      details: true,
      gallery: true,
      'behind-the-scenes': true,
      membership: true,
      // Also needs REACT_APP_AVAILABILITY_ENABLED, as before.
      schedules: true,
      // Slots: the Process Journal (replaces Behind the Scenes) and Co-hosts and publish.
      journal: false,
      'co-hosts': false,
    },
    cards: {
      // Details step: "Start from a template", "Part of <production>", "Episode defaults".
      templates: false,
      partOf: false,
      episodeDefaults: false,
    },
  },
  productionPage: {
    steps: {
      basic: true,
      gallery: true,
      team: true,
      'touring-links': true,
      // Slots: Stage and place, Process Journal, Support · $5, Live Rooms, Payouts and publish.
      stage: false,
      journal: false,
      support: false,
      'live-rooms': false,
      settings: false,
    },
    cards: {
      // Media step: the Mood Board.
      moodBoard: false,
    },
  },
});

/**
 * The steps an editor shows, in the editor's own order.
 *
 * @param {Object} flags one editor's flags, e.g. editorFlags.liveRoom
 * @param {Array<string>} steps the editor's steps in order
 * @return {Array<string>} the steps whose flag is `true`
 */
const visibleEditorSteps = (flags, steps) => steps.filter(step => flags.steps[step] === true);

/**
 * The editor's step groups, each with only its visible steps; a group left empty is dropped.
 *
 * @param {Array<Object>} groups [{ labelKey, tabs }]
 * @param {Array<string>} visibleSteps from visibleEditorSteps
 */
const visibleEditorGroups = (groups, visibleSteps) =>
  groups
    .map(group => ({ ...group, tabs: group.tabs.filter(tab => visibleSteps.includes(tab)) }))
    .filter(group => group.tabs.length > 0);

/**
 * Whether an editor shows a card. Unknown cards are hidden.
 *
 * @param {Object} flags one editor's flags, e.g. editorFlags.productionPage
 * @param {string} card the card's key
 */
const isEditorCardShown = (flags, card) => flags.cards[card] === true;

module.exports = {
  features,
  editorFlags,
  visibleEditorSteps,
  visibleEditorGroups,
  isEditorCardShown,
};
